import React, { useState, useMemo, useRef, useEffect, useLayoutEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import AudioPlayer, { AudioPlayerRef } from '../../components/learning/AudioPlayer';
import { useLearningContext } from './LearningLayout';
import { useAuth } from '../../contexts/AuthContext';
import { learningService } from '../../services/learningService';
import { useLearningSession } from '../../hooks/useLearningSession';
import { getSharedLemma, getLemmaRelationInfo } from '../../utils/lemmatizer';
import {
  transformApiDependencyGraph,
  findWordInDependencyGraph,
  ApiDependencyGraphData,
} from '../../utils/dependencyGraphUtils';
import type { ModeProgressSummary } from '../../types/learning';
import './DictationPage.css';
import './SentenceNav.css';

import { resolveMediaUrl } from '../../utils/mediaUrl';
import { containsFullWidthAlpha, toHalfWidth, isPunctuationOnly, coerceInputCase } from '../../utils/wordComparison';
import { useToastStore } from '../../stores/toastStore';
import XpToastContainer, { useXpToast } from '../../components/learning/XpToast';
import LetterPicker from '../../components/learning/LetterPicker';
import InputModeToggle from '../../components/learning/InputModeToggle';
import { useDictationInputMode } from '../../hooks/useDictationInputMode';
import { usePhonicsErrorCollector } from '../../hooks/usePhonicsErrorCollector';
import { usePhonicsGuidance } from '../../hooks/usePhonicsGuidance';
import PhonicsSessionSummary from '../../components/learning/PhonicsSessionSummary';
import DictationGuidancePanel from '../../components/learning/DictationGuidancePanel';
import type { GuidanceItem } from '../../components/learning/DictationGuidancePanel';
import { useSightWordCollector } from '../../hooks/useSightWordCollector';
import { buildErrorDataFromWordResults } from '../../utils/typingModeErrorAdapter';

interface Segment {
  id: string;
  text: string;
  audioUrl?: string;
}

interface WordResult {
  word: string;
  userInput: string;
  isCorrect: boolean;
  sentenceIndex: number;
}

interface SentenceResult {
  sentenceIndex: number;
  totalWords: number;
  correctWords: number;
  accuracy: number;
  wordResults: WordResult[];
}

/**
 * Resolves ambiguous word forms based on grammatical role from dependency graph.
 * For example, 'plural_or_third_person' becomes 'plural' for nouns/determiners
 * and 'third_person' for verbs.
 */
const resolveWordForm = (
  form: string | undefined,
  grammaticalRole?: { role: string }
): string | undefined => {
  if (!form) return undefined;

  if (form === 'plural_or_third_person') {
    // Use grammatical role to disambiguate
    if (grammaticalRole?.role === 'Verb') {
      return 'third_person';
    }
    // For Noun, Determiner, Pronoun, etc. → treat as plural
    return 'plural';
  }

  return form;
};

const DictationPage: React.FC = () => {
  const { t } = useTranslation('learning');
  const navigate = useNavigate();
  const { courseId } = useParams<{ courseId: string }>();
  const { courseData, loading, error } = useLearningContext();
  const { user } = useAuth();
  const { toasts: xpToasts, showXp } = useXpToast();
  const { isLetterPicker, toggle: toggleInputMode } = useDictationInputMode();
  const { addErrors: addPhonicsErrors, flush: flushPhonicsErrors } = usePhonicsErrorCollector(courseId, 'dictation');
  const { handleLetterError, handleSentenceComplete, getSessionErrors, getProsodyErrors } = usePhonicsGuidance();
  const { addSentence: addSightWords } = useSightWordCollector();
  const [letterPickerResetKey, setLetterPickerResetKey] = useState(0);
  // Track LetterPicker revealed positions so scoring penalizes hinted letters
  const lpRevealedRef = useRef<Set<number>>(new Set());
  const handleLpErrorData = useCallback((data: import('../../components/learning/LetterPicker').LetterPickerErrorData) => {
    if (data.revealedPositions.length > 0) {
      data.revealedPositions.forEach(p => lpRevealedRef.current.add(p));
    }
    addPhonicsErrors(data);
    // Detect prosody-related listening errors at sentence level
    handleSentenceComplete(data.expectedText, data);
    // Track sight word mastery
    addSightWords(data);
  }, [addPhonicsErrors, handleSentenceComplete, addSightWords]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [wordInputs, setWordInputs] = useState<string[]>([]);
  const [activeWordIndex, setActiveWordIndex] = useState(0);
  const [showResult, setShowResult] = useState(false);
  const [playCount, setPlayCount] = useState(0);
  const [audioSpeed, setAudioSpeed] = useState<number | undefined>(undefined);
  const [audioRepeating, setAudioRepeating] = useState(false);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const audioPlayerRef = useRef<AudioPlayerRef>(null);

  // Container ref used to retrigger the crossfade animation when the
  // sentence changes. Animating the entire input area as a single unit
  // avoids the multi-jump reflow that happens when individual flex
  // children try to morph between layouts of different sizes.
  const wordInputsContainerRef = useRef<HTMLDivElement | null>(null);

  // Lemma explanation modal state
  const [lemmaModalData, setLemmaModalData] = useState<{
    userWord: string;
    correctWord: string;
    explanation: string;
    // Word form info (e.g., 'past_tense', 'present_participle')
    userForm?: string;
    correctForm?: string;
    // Grammatical role from dependency graph
    grammaticalRole?: {
      role: string;      // 'Verb'
      roleZh: string;    // '動詞'
      color: string;     // '#22c55e'
    };
    definition?: string;                    // Word definition in context
  } | null>(null);

  // Session-level tracking
  const [sessionResults, setSessionResults] = useState<SentenceResult[]>([]);
  const [isSessionComplete, setIsSessionComplete] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [guidance, setGuidance] = useState<GuidanceItem[]>([]);

  // Progress tracking (for resume and free selection)
  const [progressSummary, setProgressSummary] = useState<ModeProgressSummary | null>(null);
  const [hasLoadedInitialPosition, setHasLoadedInitialPosition] = useState(false);
  const [showSentenceSelector, setShowSentenceSelector] = useState(false);
  const savePositionTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const fullWidthWarningRef = useRef<number>(0);

  // Learning session tracking (time spent in this mode)
  const { updateContext: updateSessionContext } = useLearningSession({
    courseId: courseId || '',
    mode: 'dictation',
    enabled: !!courseId && !!user,
    contextData: { sentenceIndex: currentIndex },
  });

  // Update session context when sentence index changes
  useEffect(() => {
    updateSessionContext({ sentenceIndex: currentIndex });
  }, [currentIndex, updateSessionContext]);

  // Convert sentences from courseData to segments
  const segments: Segment[] = useMemo(() => {
    if (!courseData?.sentences) return [];

    return courseData.sentences.map((sentence: any, index: number) => {
      const audioUrl = resolveMediaUrl(sentence.audio_url) || undefined;

      return {
        id: String(index),
        text: sentence.text,
        audioUrl: audioUrl || undefined,
      };
    });
  }, [courseData?.sentences]);

  const currentSegment = segments[currentIndex];

  // Load progress summary and set initial position
  useEffect(() => {
    if (!courseId || hasLoadedInitialPosition) return;

    const loadProgress = async () => {
      try {
        const summary = await learningService.getProgressSummary(courseId);
        setProgressSummary(summary.dictation);

        // Set initial position: resume from last position or first incomplete
        if (summary.dictation.currentIndex !== undefined && summary.dictation.currentIndex < segments.length) {
          setCurrentIndex(summary.dictation.currentIndex);
        }

        // Show sentence selector if all sentences have been completed once
        if (summary.dictation.isCompleted) {
          setShowSentenceSelector(true);
        }
      } catch (err) {
        console.error('Failed to load progress summary:', err);
      } finally {
        setHasLoadedInitialPosition(true);
      }
    };

    if (segments.length > 0) {
      loadProgress();
    }
  }, [courseId, segments.length, hasLoadedInitialPosition]);

  // Save position when changing sentences (debounced)
  useEffect(() => {
    if (!courseId || !hasLoadedInitialPosition) return;

    // Clear any pending save
    if (savePositionTimeoutRef.current) {
      clearTimeout(savePositionTimeoutRef.current);
    }

    // Debounce: save position after 1 second of no changes
    savePositionTimeoutRef.current = setTimeout(async () => {
      try {
        await learningService.savePosition(courseId, 'dictation', currentIndex);
      } catch (err) {
        console.error('Failed to save position:', err);
      }
    }, 1000);

    return () => {
      if (savePositionTimeoutRef.current) {
        clearTimeout(savePositionTimeoutRef.current);
      }
    };
  }, [courseId, currentIndex, hasLoadedInitialPosition]);

  // Normalize text for display (remove markdown symbols but keep readable)
  const normalizeForDisplay = (text: string): string => {
    return text
      .replace(/^[#*\->\s]+/, '') // Remove leading markdown symbols
      .trim();
  };

  // Get the words for the current segment
  // Split hyphenated words like "thirty-two" into separate inputs
  const correctWords = useMemo(() => {
    if (!currentSegment) return [];
    const displayText = normalizeForDisplay(currentSegment.text);
    const rawWords = displayText.split(/\s+/).filter(w => w.length > 0);

    // Expand hyphenated words: "thirty-two" → ["thirty-", "two"]
    const expandedWords: string[] = [];
    for (const word of rawWords) {
      // Check if word contains hyphen (but not at start/end, and not abbreviations like "e-mail")
      // Split on hyphens, keeping the hyphen attached to the preceding part
      if (word.includes('-') && !word.startsWith('-') && !word.endsWith('-')) {
        const parts = word.split('-');
        parts.forEach((part, idx) => {
          if (idx < parts.length - 1) {
            // Add hyphen to all parts except the last
            expandedWords.push(part + '-');
          } else {
            expandedWords.push(part);
          }
        });
      } else {
        expandedWords.push(word);
      }
    }

    return expandedWords;
  }, [currentSegment]);

  // Replay the crossfade animation whenever the user moves to a new
  // sentence. We retrigger by toggling a class — `key` remount would
  // wipe input refs and focus state.
  const lastAnimatedIndexRef = useRef<number>(-1);
  useLayoutEffect(() => {
    if (lastAnimatedIndexRef.current === currentIndex) return;
    lastAnimatedIndexRef.current = currentIndex;
    const container = wordInputsContainerRef.current;
    if (!container) return;
    const prefersReducedMotion = typeof window !== 'undefined'
      && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    if (prefersReducedMotion) return;
    container.classList.remove('word-inputs-container--enter');
    // Force reflow so removing+re-adding the class restarts the animation.
    void container.offsetHeight;
    container.classList.add('word-inputs-container--enter');
  }, [currentIndex]);

  // Normalize a single word for comparison
  // - Remove diacritics (é → e, ñ → n, etc.)
  // - Remove leading/trailing quotes and punctuation (including hyphens)
  // - Preserve abbreviations like "p.m.", "a.m." but remove trailing punctuation from regular words
  // - Case-sensitive comparison (no toLowerCase)
  const normalizeWord = useCallback((word: string): string => {
    if (!word) return '';

    const normalized = word
      .normalize('NFD')                    // Decompose accented characters (é → e + ́)
      .replace(/[\u0300-\u036f]/g, '')     // Remove diacritical marks
      .replace(/^[^\w']+/, '')             // Remove leading punctuation (including quotes)
      .replace(/[""'']+$/, '');            // Remove trailing quotes (smart and straight)

    // Check if word is an abbreviation pattern like "a.m." or "p.m."
    // Pattern: letter.letter. (with optional trailing punctuation like ?)
    const abbrevPattern = /^[a-zA-Z](\.[a-zA-Z])+\.?[!?,;:]*$/;

    if (abbrevPattern.test(normalized)) {
      // For abbreviations, only remove non-period trailing punctuation
      return normalized.replace(/[!?,;:]+$/, '');
    } else {
      // For regular words, remove all trailing punctuation including periods and hyphens
      return normalized.replace(/[.!?,;:-]+$/, '');
    }
  }, []);

  // Check if a word input is correct (full word comparison)
  const isWordCorrect = useCallback((inputWord: string, correctWord: string): boolean => {
    if (!inputWord || !correctWord) return false;
    return normalizeWord(inputWord) === normalizeWord(correctWord);
  }, [normalizeWord]);

  // Initialize word inputs when segment changes
  useEffect(() => {
    if (correctWords.length > 0) {
      setWordInputs(new Array(correctWords.length).fill(''));
      // Start focus on first inputable word (skip punctuation-only tokens like "…", "—")
      const firstInputIdx = correctWords.findIndex(w => /[a-zA-Z0-9]/.test(w));
      setActiveWordIndex(firstInputIdx >= 0 ? firstInputIdx : 0);
      inputRefs.current = new Array(correctWords.length).fill(null);
    }
  }, [correctWords.length, currentIndex]);

  // Focus active input
  useEffect(() => {
    if (!showResult && inputRefs.current[activeWordIndex]) {
      inputRefs.current[activeWordIndex]?.focus();
    }
  }, [activeWordIndex, showResult]);


  // Calculate accuracy (excluding punctuation-only tokens)
  const calculateAccuracy = useCallback((): number => {
    const inputableWords = correctWords.filter(w => !isPunctuationOnly(w));
    if (inputableWords.length === 0) return 100;
    const correctCount = wordInputs.filter((input, idx) =>
      !isPunctuationOnly(correctWords[idx]) && isWordCorrect(input, correctWords[idx])
    ).length;
    return Math.round((correctCount / inputableWords.length) * 100);
  }, [wordInputs, correctWords, isWordCorrect]);

  // Get the max length for a word (use normalized length so typing the word without punctuation triggers auto-advance)
  const getWordMaxLength = useCallback((word: string): number => {
    // Use normalized word length so users typing "lunch" will auto-advance even if correct word is "lunch."
    // But keep original length for abbreviations like "p.m." so they need to type the full form
    return normalizeWord(word).length;
  }, [normalizeWord]);

  // Extract leading punctuation/quotes from a word (to display before input)
  // Matches any leading non-word characters: quotes, currency symbols ($€£¥), etc.
  const getLeadingPunctuation = useCallback((word: string): string => {
    const match = word.match(/^[^\w']+/);
    return match ? match[0] : '';
  }, []);

  // Extract trailing punctuation from a word (to display after input)
  const getTrailingPunctuation = useCallback((word: string): string => {
    // Check for abbreviation pattern (e.g., "p.m.", "a.m.")
    const abbrevPattern = /^[a-zA-Z](\.[a-zA-Z])+\.?[!?,;:]*$/;
    if (abbrevPattern.test(word)) {
      // For abbreviations, only extract non-period trailing punctuation
      const match = word.match(/[!?,;:]+$/);
      return match ? match[0] : '';
    }
    // For regular words, extract all trailing punctuation including quotes and hyphens
    const match = word.match(/[.!?,;:\-""''「」『』)\]}>]+$/);
    return match ? match[0] : '';
  }, []);

  // Handle input change for a specific word
  const handleWordChange = (index: number, value: string) => {
    // Detect full-width characters and warn user
    if (containsFullWidthAlpha(value)) {
      const now = Date.now();
      if (now - fullWidthWarningRef.current > 3000) {
        fullWidthWarningRef.current = now;
        useToastStore.getState().warning(t('dictation.fullWidthWarning'), 3000);
      }
      value = toHalfWidth(value);
    }

    // Don't allow spaces in individual word inputs
    const cleanValue = value.replace(/\s/g, '');

    // Neutralise Android autocapitalize — coerce case across the whole input
    // because some IMEs (e.g. Huawei Browser) capitalise several characters,
    // not just the first one, even when autoCapitalize="none" is set.
    const casedValue = coerceInputCase(cleanValue, correctWords[index] || '');

    // Get the max length for this word
    const maxLen = getWordMaxLength(correctWords[index]);

    // Truncate if exceeds max length
    const truncatedValue = casedValue.slice(0, maxLen);

    const newInputs = [...wordInputs];
    newInputs[index] = truncatedValue;
    setWordInputs(newInputs);

    // Auto-advance to next word when reaching max length (skip punctuation-only tokens)
    if (truncatedValue.length >= maxLen && index < correctWords.length - 1) {
      setTimeout(() => {
        let nextIdx = index + 1;
        while (nextIdx < correctWords.length && isPunctuationOnly(correctWords[nextIdx])) {
          nextIdx++;
        }
        if (nextIdx < correctWords.length) {
          setActiveWordIndex(nextIdx);
        }
      }, 100);
    }
  };

  // Handle key down events
  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    const input = wordInputs[index];

    // Space key - toggle audio play/pause
    if (e.key === ' ') {
      e.preventDefault();
      audioPlayerRef.current?.togglePlayPause();
    }

    // Backspace at the beginning - move to previous word (skip punctuation-only)
    if (e.key === 'Backspace' && input === '' && index > 0) {
      e.preventDefault();
      let prevIdx = index - 1;
      while (prevIdx >= 0 && isPunctuationOnly(correctWords[prevIdx])) {
        prevIdx--;
      }
      if (prevIdx >= 0) {
        setActiveWordIndex(prevIdx);
      }
    }

    // Enter key - check answer or continue to next
    if (e.key === 'Enter') {
      e.preventDefault();
      if (showResult) {
        const isLast = currentIndex === segments.length - 1;
        if (isLast) {
          handleCompleteSession();
        } else {
          handleNext();
        }
      } else {
        handleSubmit();
      }
    }

    // Tab key - move between words (skip punctuation-only)
    if (e.key === 'Tab') {
      e.preventDefault();
      if (e.shiftKey) {
        let prevIdx = index - 1;
        while (prevIdx >= 0 && isPunctuationOnly(correctWords[prevIdx])) {
          prevIdx--;
        }
        if (prevIdx >= 0) setActiveWordIndex(prevIdx);
      } else {
        let nextIdx = index + 1;
        while (nextIdx < correctWords.length && isPunctuationOnly(correctWords[nextIdx])) {
          nextIdx++;
        }
        if (nextIdx < correctWords.length) setActiveWordIndex(nextIdx);
      }
    }
  };

  // Record current sentence result (excluding punctuation-only tokens)
  const recordSentenceResult = useCallback(() => {
    const wordResults: WordResult[] = correctWords
      .map((word, idx) => ({
        word,
        userInput: wordInputs[idx] || '',
        isCorrect: isWordCorrect(wordInputs[idx] || '', word),
        sentenceIndex: currentIndex,
      }))
      .filter(wr => !isPunctuationOnly(wr.word));

    const correctCount = wordResults.filter(r => r.isCorrect).length;
    const result: SentenceResult = {
      sentenceIndex: currentIndex,
      totalWords: wordResults.length,
      correctWords: correctCount,
      accuracy: wordResults.length > 0 ? Math.round((correctCount / wordResults.length) * 100) : 100,
      wordResults,
    };

    setSessionResults(prev => {
      // Replace if already exists for this sentence, otherwise add
      const existing = prev.findIndex(r => r.sentenceIndex === currentIndex);
      if (existing >= 0) {
        const updated = [...prev];
        updated[existing] = result;
        return updated;
      }
      return [...prev, result];
    });

    return result;
  }, [correctWords, wordInputs, currentIndex, isWordCorrect]);

  // Save word mastery to API
  const saveWordMastery = useCallback(async (results: SentenceResult[]) => {
    if (!courseId) return;

    try {
      setIsSaving(true);

      // Collect all word results
      const allWordResults = results.flatMap(r => r.wordResults);

      const result = await learningService.saveDictationResults({
        courseId,
        inputMode: isLetterPicker ? 'letter_picker' : 'typing',
        results: allWordResults.map(wr => ({
          word: wr.word,
          userInput: wr.userInput,
          isCorrect: wr.isCorrect,
          sentenceIndex: wr.sentenceIndex,
        })),
      });

      if (result.guidance && result.guidance.length > 0) {
        setGuidance(result.guidance);
      }
    } catch (err) {
      console.error('Failed to save word mastery:', err);
    } finally {
      setIsSaving(false);
    }
  }, [courseId, isLetterPicker]);

  const handleSubmit = async () => {
    if (!currentSegment || !courseId) return;
    const result = recordSentenceResult();
    setShowResult(true);

    // In typing mode, synthesize LetterPickerErrorData so phonics/prosody/sight-word
    // collectors all work the same as in LetterPicker mode
    if (!isLetterPicker) {
      const syntheticErrorData = buildErrorDataFromWordResults(
        currentSegment.text,
        result.wordResults,
      );
      addPhonicsErrors(syntheticErrorData);
      handleSentenceComplete(syntheticErrorData.expectedText, syntheticErrorData);
      addSightWords(syntheticErrorData);
    }

    // Save word mastery for this sentence and fetch guidance
    try {
      const masteryResult = await learningService.saveDictationResults({
        courseId,
        inputMode: isLetterPicker ? 'letter_picker' : 'typing',
        results: result.wordResults.map(wr => ({
          word: wr.word,
          userInput: wr.userInput,
          isCorrect: wr.isCorrect,
          sentenceIndex: wr.sentenceIndex,
        })),
      });
      if (masteryResult.guidance && masteryResult.guidance.length > 0) {
        setGuidance(masteryResult.guidance);
      }
    } catch (err) {
      console.error('Failed to save dictation results:', err);
    }

    // Call completeSentence API to track progress and update best score
    try {
      const completionResult = await learningService.completeSentence(
        courseId,
        'dictation',
        currentIndex,
        result.accuracy,
        segments.length
      );

      // Show XP toast
      if (completionResult.xpEarned > 0) {
        showXp(completionResult.xpEarned, completionResult.leveledUp, completionResult.newLevel);
      }

      // Update local progress summary
      if (completionResult.isNewBestScore || completionResult.isNewOverallBest) {
        const updatedSummary = await learningService.getProgressSummary(courseId);
        setProgressSummary(updatedSummary.dictation);

        // If all sentences are now completed, show the sentence selector
        if (updatedSummary.dictation.isCompleted) {
          setShowSentenceSelector(true);
        }
      }
    } catch (err) {
      console.error('Failed to save sentence completion:', err);
    }
  };

  const handleNext = useCallback(() => {
    if (currentIndex < segments.length - 1) {
      setCurrentIndex(currentIndex + 1);
      setShowResult(false);
      setGuidance([]);
      setPlayCount(0);
      setLetterPickerResetKey(k => k + 1);
    }
  }, [currentIndex, segments.length]);

  // Complete session (after last sentence)
  const handleCompleteSession = useCallback(async () => {
    recordSentenceResult();

    // Save all results to API
    const allResults = [...sessionResults];
    // Add current result if not already added
    const currentResult = sessionResults.find(r => r.sentenceIndex === currentIndex);
    if (!currentResult) {
      const wordResults: WordResult[] = correctWords
        .map((word, idx) => ({
          word,
          userInput: wordInputs[idx] || '',
          isCorrect: isWordCorrect(wordInputs[idx] || '', word),
          sentenceIndex: currentIndex,
        }))
        .filter(wr => !isPunctuationOnly(wr.word));
      const correctCount = wordResults.filter(r => r.isCorrect).length;
      allResults.push({
        sentenceIndex: currentIndex,
        totalWords: wordResults.length,
        correctWords: correctCount,
        accuracy: wordResults.length > 0 ? Math.round((correctCount / wordResults.length) * 100) : 100,
        wordResults,
      });
    }

    await saveWordMastery(allResults);
    setIsSessionComplete(true);
  }, [recordSentenceResult, sessionResults, currentIndex, correctWords, wordInputs, isWordCorrect, saveWordMastery]);

  const handleTryAgain = () => {
    setWordInputs(new Array(correctWords.length).fill(''));
    setActiveWordIndex(0);
    setShowResult(false);
    setGuidance([]);
    setPlayCount(0);
    setLetterPickerResetKey(k => k + 1);
  };

  // Restart entire practice
  const handleRestartPractice = () => {
    setCurrentIndex(0);
    setSessionResults([]);
    setIsSessionComplete(false);
    setShowResult(false);
    setPlayCount(0);
    setWordInputs([]);
    setActiveWordIndex(0);
    setLetterPickerResetKey(k => k + 1);
  };

  // Jump to a specific sentence (for free selection mode)
  const handleJumpToSentence = (index: number) => {
    setCurrentIndex(index);
    setShowResult(false);
    setPlayCount(0);
    setWordInputs([]);
    setActiveWordIndex(0);
    setLetterPickerResetKey(k => k + 1);
  };

  // Go to next learning mode
  const handleNextMode = () => {
    navigate(`/courses/${courseId}/shadowing`);
  };

  // Global keyboard handler for Enter when result is shown
  useEffect(() => {
    const handleGlobalKeyDown = (e: KeyboardEvent) => {
      // Skip if the event originated from an input element (already handled by input's onKeyDown)
      if (e.target instanceof HTMLInputElement) {
        return;
      }

      const isLast = currentIndex === segments.length - 1;
      if (e.key === 'Enter' && showResult && !isSessionComplete) {
        e.preventDefault();
        if (isLast) {
          handleCompleteSession();
        } else {
          handleNext();
        }
      }
    };

    document.addEventListener('keydown', handleGlobalKeyDown);
    return () => document.removeEventListener('keydown', handleGlobalKeyDown);
  }, [showResult, isSessionComplete, currentIndex, segments.length, handleCompleteSession, handleNext]);

  // Calculate overall session stats
  const sessionStats = useMemo(() => {
    if (sessionResults.length === 0) return { totalWords: 0, correctWords: 0, accuracy: 0 };

    const totalWords = sessionResults.reduce((sum, r) => sum + r.totalWords, 0);
    const correctWords = sessionResults.reduce((sum, r) => sum + r.correctWords, 0);
    const accuracy = totalWords > 0 ? Math.round((correctWords / totalWords) * 100) : 0;

    return { totalWords, correctWords, accuracy };
  }, [sessionResults]);

  // Memoize lemma explanations to avoid recalculating on every render
  // Now stores full info including word forms for display
  interface LemmaExplanationData {
    explanation: string;
    userForm: string;
    correctForm: string;
  }
  const lemmaExplanations = useMemo(() => {
    if (!showResult) return new Map<number, LemmaExplanationData>();

    const explanations = new Map<number, LemmaExplanationData>();
    correctWords.forEach((correctWord, idx) => {
      if (isPunctuationOnly(correctWord)) return; // Skip punctuation-only tokens
      const userInput = wordInputs[idx] || '';
      if (userInput && !isWordCorrect(userInput, correctWord)) {
        const normalizedUser = normalizeWord(userInput);
        const normalizedCorrect = normalizeWord(correctWord);

        // Skip if only difference is capitalization (e.g., "midas" vs "Midas")
        if (normalizedUser.toLowerCase() === normalizedCorrect.toLowerCase()) {
          return; // This is a capitalization issue, not a word form issue
        }

        const sharedLemma = getSharedLemma(normalizedUser, normalizedCorrect);
        if (sharedLemma) {
          // Look up the word's form and role from the dependency graph
          const apiGraph = courseData?.sentences?.[currentIndex]?.dependency_graph;
          const graphWords = apiGraph?.words as Array<{ word: string; role: string; form?: string }> | undefined;
          const wordNode = graphWords?.find(
            w => w.word.toLowerCase() === normalizedCorrect.toLowerCase()
          );
          const info = getLemmaRelationInfo(normalizedUser, normalizedCorrect, sharedLemma, wordNode?.role, wordNode?.form);
          const explanation = t(`dictation.lemmaHint.${info.translationKey}`, {
            defaultValue: `${info.userWord} ≠ ${info.correctWord}`,
            userWord: info.userWord,
            correctWord: info.correctWord,
            lemma: info.lemma,
          });
          explanations.set(idx, {
            explanation,
            userForm: info.userForm,
            correctForm: info.correctForm,
          });
        }
      }
    });
    return explanations;
  }, [showResult, wordInputs, correctWords, isWordCorrect, normalizeWord, t, courseData?.sentences, currentIndex]);

  // Render word input boxes
  // Bridge: LetterPicker completes → populate wordInputs and submit
  // Words that had any letter revealed by hint/auto-reveal are left empty
  // so the scoring logic correctly marks them as incorrect.
  const handleLetterPickerComplete = useCallback((_userText: string) => {
    if (!currentSegment) return;
    const revealed = lpRevealedRef.current;

    // Map character positions in expectedText back to word indices
    const displayText = normalizeForDisplay(currentSegment.text);
    const wordsWithRevealed = new Set<number>();
    if (revealed.size > 0) {
      let charIdx = 0;
      let wordIdx = 0;
      for (let i = 0; i < displayText.length; i++) {
        if (displayText[i] === ' ') {
          wordIdx++;
        } else {
          if (revealed.has(charIdx)) {
            wordsWithRevealed.add(wordIdx);
          }
        }
        charIdx++;
      }
    }

    const newInputs = correctWords.map((cw, idx) => {
      if (isPunctuationOnly(cw)) return '';
      if (wordsWithRevealed.has(idx)) return '';
      return normalizeWord(cw);
    });
    setWordInputs(newInputs);
    lpRevealedRef.current = new Set();
    setTimeout(() => handleSubmit(), 300);
  }, [currentSegment, correctWords, normalizeWord, handleSubmit]);

  const renderWordInputs = () => {
    return (
      <div className="word-inputs-container" ref={wordInputsContainerRef}>
        {correctWords.map((correctWord, idx) => {
          // Punctuation-only token (e.g., "…", "—", "–") — display as static text, not an input
          if (isPunctuationOnly(correctWord)) {
            return (
              <span key={idx} className="word-punctuation-display">{correctWord}</span>
            );
          }

          const userInput = wordInputs[idx] || '';
          const isEmpty = userInput === '';
          const isActive = idx === activeWordIndex;

          // Full word comparison for result display
          const isCorrect = isWordCorrect(userInput, correctWord);

          // Get memoized lemma explanation
          const lemmaExplanation = lemmaExplanations.get(idx);

          // Calculate input width based on normalized word length (without trailing punctuation)
          const charWidth = 14; // pixels per character
          const padding = 32;   // account for CSS padding (24px) + border (4px) + buffer
          const normalizedLen = normalizeWord(correctWord).length;
          const minWidth = Math.max(normalizedLen * charWidth + padding, 60);

          // Get max length for this word (without punctuation)
          const maxLen = getWordMaxLength(correctWord);

          // Get leading and trailing punctuation to display around the input
          const leadingPunct = getLeadingPunctuation(correctWord);
          const trailingPunct = getTrailingPunctuation(correctWord);

          // Only apply coloring when showing result (not during live typing)
          const inputClassName = `word-input ${isActive ? 'word-input--active' : ''} ${showResult && isCorrect ? 'word-input--correct' : ''} ${showResult && !isCorrect && !isEmpty ? 'word-input--incorrect' : ''}`;

          return (
            <div key={idx} className="word-input-wrapper">
              {leadingPunct && (
                <span className="word-punctuation word-punctuation--leading">{leadingPunct}</span>
              )}
              <input
                ref={(el) => { inputRefs.current[idx] = el; }}
                type="text"
                name={`dictation-word-${currentIndex}-${idx}`}
                className={inputClassName}
                value={userInput}
                onChange={(e) => handleWordChange(idx, e.target.value)}
                onKeyDown={(e) => handleKeyDown(idx, e)}
                onFocus={() => setActiveWordIndex(idx)}
                disabled={showResult}
                style={{ width: `${minWidth}px` }}
                maxLength={maxLen}
                autoComplete="off"
                autoCorrect="off"
                autoCapitalize="none"
                inputMode="text"
                spellCheck={false}
                data-1p-ignore="true"
                data-lpignore="true"
                data-form-type="other"
              />
              {trailingPunct && (
                <span className="word-punctuation">{trailingPunct}</span>
              )}
              {showResult && !isCorrect && (
                <div className="word-hint">
                  {normalizeWord(correctWord)}
                  {lemmaExplanation && (
                    <button
                      className="word-lemma-hint-btn"
                      onClick={() => {
                        // Get dependency graph data for grammatical role
                        const sentence = courseData?.sentences?.[currentIndex];
                        const apiGraph = sentence?.dependency_graph;

                        let grammaticalRole: { role: string; roleZh: string; color: string } | undefined;
                        let definition: string | undefined;

                        // Validate API data structure before transformation
                        const isValidGraph = apiGraph &&
                          Array.isArray(apiGraph.words) &&
                          Array.isArray(apiGraph.arcs) &&
                          Array.isArray(apiGraph.animation_steps);

                        if (isValidGraph) {
                          // Transform API data to component format
                          const dependencyGraph = transformApiDependencyGraph(apiGraph as ApiDependencyGraphData);

                          // Find the word in the dependency graph
                          const wordMapping = findWordInDependencyGraph(idx, correctWords, dependencyGraph);

                          if (wordMapping.graphNode) {
                            grammaticalRole = {
                              role: wordMapping.graphNode.role,
                              roleZh: wordMapping.graphNode.roleZh,
                              color: wordMapping.graphNode.color,
                            };

                            // Use definition from dependency graph if available (new approach)
                            if (wordMapping.graphNode.definitionZh) {
                              definition = wordMapping.graphNode.definitionZh;
                            }
                          }
                        }

                        // Fallback: Look up word definition from vocabulary if not found in dependency graph
                        if (!definition) {
                          const normalizedCorrectWord = normalizeWord(correctWord).toLowerCase();
                          // Try comprehensive vocabulary first (has context-specific definitions)
                          const compVocab = courseData?.comprehensiveVocabulary?.find(
                            v => v.word.toLowerCase() === normalizedCorrectWord
                          );
                          if (compVocab) {
                            // Prefer context-specific definition, fallback to general
                            definition = compVocab.definition_in_context || compVocab.general_definition;
                          } else {
                            // Fallback to basic vocabulary
                            const basicVocab = courseData?.vocabulary?.find(
                              v => v.word.toLowerCase() === normalizedCorrectWord
                            );
                            if (basicVocab) {
                              definition = basicVocab.definitionNative;
                            }
                          }
                        }

                        setLemmaModalData({
                          userWord: userInput,
                          correctWord: normalizeWord(correctWord),
                          explanation: lemmaExplanation.explanation,
                          userForm: lemmaExplanation.userForm,
                          correctForm: lemmaExplanation.correctForm,
                          grammaticalRole,
                          definition,
                        });
                      }}
                      title={t('dictation.lemmaHint.clickForDetails')}
                    >
                      ?
                    </button>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  const dictationResetState = useCallback(() => {
    setWordInputs(new Array(correctWords.length).fill(''));
    setActiveWordIndex(0);
    setShowResult(false);
    setGuidance([]);
    setPlayCount(0);
    setLetterPickerResetKey(k => k + 1);
  }, [correctWords.length]);


  // Show loading state
  if (loading) {
    return (
      <div className="dictation-page">
        <div className="dictation-loading">{t('dictation.loading')}</div>
      </div>
    );
  }

  // Show error state
  if (error) {
    return (
      <div className="dictation-page">
        <div className="dictation-error">
          <h3>{t('dictation.errorTitle')}</h3>
          <p>{error}</p>
        </div>
      </div>
    );
  }

  // No sentences available
  if (segments.length === 0) {
    return (
      <div className="dictation-page">
        <div className="dictation-empty">
          <h3>{t('dictation.noSentencesTitle')}</h3>
          <p>{t('dictation.noSentencesDesc')}</p>
        </div>
      </div>
    );
  }

  if (!currentSegment) {
    return (
      <div className="dictation-page">
        <div className="dictation-loading">{t('dictation.loadingSimple')}</div>
      </div>
    );
  }

  const accuracy = calculateAccuracy();
  const isLastSentence = currentIndex === segments.length - 1;

  // Session Complete Screen
  if (isSessionComplete) {
    return (
      <div className="dictation-page">
        <div className="session-complete">
          <div className="session-complete-header">
            <span className="session-complete-icon">&#10003;</span>
            <h2>{t('dictation.complete.title')}</h2>
            <p>{t('dictation.complete.subtitle')}</p>
          </div>

          <div className="session-stats">
            <div className="stat-card stat-card--main">
              <span className="stat-value">{sessionStats.accuracy}%</span>
              <span className="stat-label">{t('dictation.complete.overallAccuracy')}</span>
            </div>
            <div className="stat-row">
              <div className="stat-card">
                <span className="stat-value">{sessionStats.correctWords}</span>
                <span className="stat-label">{t('dictation.complete.correctWords')}</span>
              </div>
              <div className="stat-card">
                <span className="stat-value">{sessionStats.totalWords}</span>
                <span className="stat-label">{t('dictation.complete.totalWords')}</span>
              </div>
              <div className="stat-card">
                <span className="stat-value">{sessionResults.length}</span>
                <span className="stat-label">{t('dictation.complete.completedSentences')}</span>
              </div>
            </div>
          </div>

          {/* Per-sentence breakdown */}
          <div className="sentence-breakdown">
            <h3>{t('dictation.complete.sentenceScores')}</h3>
            <div className="sentence-list">
              {sessionResults.map((result, idx) => (
                <div key={idx} className="sentence-row">
                  <span className="sentence-num">#{result.sentenceIndex + 1}</span>
                  <div className="sentence-bar">
                    <div
                      className="sentence-bar-fill"
                      style={{
                        width: `${result.accuracy}%`,
                        backgroundColor: result.accuracy >= 80 ? '#28a745' : result.accuracy >= 60 ? '#ffc107' : '#dc3545',
                      }}
                    />
                  </div>
                  <span className="sentence-score">{result.accuracy}%</span>
                </div>
              ))}
            </div>
          </div>

          {/* Word mastery saved indicator */}
          {isSaving ? (
            <div className="saving-indicator">{t('dictation.complete.saving')}</div>
          ) : (
            <div className="saved-indicator">✓ {t('dictation.complete.saved')}</div>
          )}

          <PhonicsSessionSummary errors={getSessionErrors()} prosodyErrors={getProsodyErrors()} />

          <div className="session-actions">
            <button className="action-button action-button--retry" onClick={handleRestartPractice}>
              🔄 {t('dictation.complete.restart')}
            </button>
            <button className="action-button action-button--next" onClick={handleNextMode}>
              ➡️ {t('dictation.complete.nextMode')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="dictation-page">
      <div className="learning-mode-header">
        <h2 className="learning-mode-header__title">{t('dictation.title')}</h2>
        <InputModeToggle isLetterPicker={isLetterPicker} onToggle={() => { toggleInputMode(); setLetterPickerResetKey(k => k + 1); }} />
        <div className="learning-mode-header__nav">
          <button
            className="sentence-nav-btn"
            onClick={() => { if (currentIndex > 0) { setCurrentIndex(currentIndex - 1); dictationResetState(); } }}
            disabled={currentIndex === 0}
            aria-label={t('navigation.previousSentence')}
          >
            ◀
          </button>
          <span className="sentence-nav-counter">{currentIndex + 1} / {segments.length}</span>
          <button
            className="sentence-nav-btn"
            onClick={handleNext}
            disabled={isLastSentence}
            aria-label={t('navigation.nextSentence')}
          >
            ▶
          </button>
        </div>
      </div>

      <div className="dictation-content">
        {/* Instructions */}
        <div className="dictation-instructions">
          <p>{t('dictation.instructions')}</p>
        </div>

        {/* Sentence Selector (when all sentences completed) */}
        {showSentenceSelector && (
          <div className="sentence-selector">
            <div className="sentence-selector-header">
              <span>🎯 {t('dictation.sentenceSelector.header')}</span>
              <button
                className="toggle-selector-btn"
                onClick={() => setShowSentenceSelector(!showSentenceSelector)}
              >
                {showSentenceSelector ? t('dictation.sentenceSelector.hide') : t('dictation.sentenceSelector.show')}
              </button>
            </div>
            <div className="sentence-selector-grid">
              {segments.map((segment, idx) => {
                const sentenceScore = progressSummary?.sentenceScores?.find(s => s.sentenceIndex === idx);
                const scoreClass = sentenceScore
                  ? sentenceScore.score >= 80 ? 'score-excellent'
                    : sentenceScore.score >= 60 ? 'score-good'
                    : 'score-needs-work'
                  : '';
                return (
                  <button
                    key={idx}
                    className={`sentence-btn ${idx === currentIndex ? 'active' : ''} ${scoreClass}`}
                    onClick={() => handleJumpToSentence(idx)}
                  >
                    <span className="btn-number">{idx + 1}</span>
                    {sentenceScore && (
                      <span className="btn-score">{Math.round(sentenceScore.score)}%</span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* No Audio Warning */}
        {!currentSegment.audioUrl && (
          <div className="no-audio-warning">
            <span>⚠️</span>
            <span>{t('dictation.noAudio')}</span>
          </div>
        )}

        {/* Audio + Input Area */}
        <div className="dictation-practice-area">
          <div className="dictation-audio">
            <AudioPlayer
              ref={audioPlayerRef}
              audioUrl={currentSegment.audioUrl}
              onPlay={() => setPlayCount(playCount + 1)}
              speakingWpm={courseData?.course?.speakingWpm}
              autoNaturalSpeed={user?.autoNaturalSpeed}
              speed={audioSpeed}
              onSpeedChange={setAudioSpeed}
              repeating={audioRepeating}
              onRepeatingChange={setAudioRepeating}
            />
            {/* Accuracy result - shows after checking */}
            {showResult && (
              <div className="dictation-result">
                <span className={`accuracy-score ${accuracy >= 80 ? 'accuracy-score--good' : accuracy >= 60 ? 'accuracy-score--ok' : 'accuracy-score--poor'}`}>
                  {t('dictation.accuracy', { percent: accuracy })}
                </span>
                {accuracy === 100 && (
                  <span className="perfect-badge">&#10003; {t('dictation.perfect')}</span>
                )}
              </div>
            )}
          </div>
          <div className="dictation-input-area">
            {isLetterPicker && currentSegment && !showResult ? (
              <LetterPicker
                expectedText={normalizeForDisplay(currentSegment.text)}
                onComplete={handleLetterPickerComplete}
                onErrorData={handleLpErrorData}
                onLetterError={handleLetterError}
                disabled={showResult}
                resetKey={letterPickerResetKey}
              />
            ) : (
              renderWordInputs()
            )}
          </div>
        </div>

        {showResult && guidance.length > 0 && (
          <DictationGuidancePanel items={guidance} />
        )}

        {/* Action Buttons */}
        <div className="dictation-actions">
          {!showResult ? (
            <button
              className="action-button action-button--submit"
              onClick={handleSubmit}
              disabled={wordInputs.every(w => w === '')}
            >
              {t('dictation.checkAnswer')}
            </button>
          ) : (
            <>
              <button
                className="action-button action-button--retry"
                onClick={handleTryAgain}
              >
                {t('dictation.tryAgain')}
              </button>
              {isLastSentence ? (
                <button
                  className="action-button action-button--complete"
                  onClick={handleCompleteSession}
                  disabled={isSaving}
                >
                  {isSaving ? t('dictation.complete.saving') : `✓ ${t('dictation.complete.finish')}`}
                </button>
              ) : (
                <button
                  className="action-button action-button--next"
                  onClick={handleNext}
                >
                  {t('dictation.nextSentence')}
                </button>
              )}
            </>
          )}
        </div>
      </div>

      {/* Lemma Explanation Modal */}
      {lemmaModalData && (
        <div className="lemma-modal-overlay" onClick={() => setLemmaModalData(null)}>
          <div className="lemma-modal lemma-modal--enhanced" onClick={(e) => e.stopPropagation()}>
            <div className="lemma-modal-header">
              <h3>{t('dictation.lemmaHint.modalTitle')}</h3>
              <button
                className="lemma-modal-close"
                onClick={() => setLemmaModalData(null)}
              >
                ✕
              </button>
            </div>
            <div className="lemma-modal-content">
              {/* Word comparison with word forms */}
              <div className="lemma-modal-comparison">
                <div className="lemma-word lemma-word--user">
                  <span className="lemma-label">{t('dictation.lemmaHint.yourAnswer')}</span>
                  <span className="lemma-value">{lemmaModalData.userWord}</span>
                  {(() => {
                    const resolvedForm = resolveWordForm(lemmaModalData.userForm, lemmaModalData.grammaticalRole);
                    return resolvedForm && (
                      <span className="lemma-form">{t(`dictation.lemmaHint.forms.${resolvedForm}`)}</span>
                    );
                  })()}
                </div>
                <span className="lemma-arrow">→</span>
                <div className="lemma-word lemma-word--correct">
                  <span className="lemma-label">{t('dictation.lemmaHint.correctAnswer')}</span>
                  <span className="lemma-value">{lemmaModalData.correctWord}</span>
                  {(() => {
                    const resolvedForm = resolveWordForm(lemmaModalData.correctForm, lemmaModalData.grammaticalRole);
                    return resolvedForm && (
                      <span className="lemma-form">{t(`dictation.lemmaHint.forms.${resolvedForm}`)}</span>
                    );
                  })()}
                </div>
              </div>

              {/* Grammatical role badge */}
              {lemmaModalData.grammaticalRole && (
                <div className="lemma-modal-role">
                  <span className="lemma-role-label">{t('dictation.lemmaHint.grammaticalRole')}</span>
                  <span
                    className="lemma-role-badge"
                    style={{ backgroundColor: lemmaModalData.grammaticalRole.color }}
                  >
                    {lemmaModalData.grammaticalRole.roleZh} ({lemmaModalData.grammaticalRole.role})
                  </span>
                </div>
              )}

              {/* Explanation text with optional definition */}
              <div className="lemma-modal-explanation">
                {lemmaModalData.explanation}
                {lemmaModalData.definition && (
                  <div className="lemma-modal-definition">
                    {t('dictation.lemmaHint.meaning')}{lemmaModalData.definition}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
      <XpToastContainer toasts={xpToasts} />
    </div>
  );
};

export default DictationPage;
